import React from "react";

import {
  addDoc,
  arrayRemove,
  arrayUnion,
  collection,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  getFirestore,
  limit,
  onSnapshot,
  orderBy,
  query,
  startAfter,
  updateDoc,
  where,
} from "firebase/firestore";

import app from "../firebase";

import { Dev } from "../util/consoleUtil";
import firebaseError from "../util/firebaseError";

import { ChatItem, ChatMessageItem, SpottPostType } from "../../types/database";

const db = getFirestore(app);

export const ChatService = {
  One: () => {
    const [isLoading, setLoading] = React.useState(false);

    const [item, setItem] = React.useState<ChatItem | null>(null);

    const runRequest = async (itemId: string) => {
      return new Promise(async (Ok, Rej) => {
        try {
          setLoading(true);

          const mainRef = doc(db, "chats", itemId);
          const mainSnap = await getDoc(mainRef);

          const itemObj = { id: itemId, ...mainSnap.data() } as ChatItem;
          setLoading(false);

          if (!mainSnap.exists()) return Rej(new Error("Item não existe."));

          setItem(itemObj);

          Ok(itemObj);
        } catch (error) {
          console.log(...new Dev("Request Error [one]", error).Error());
          setLoading(false);
          Rej(new Error("Erro ao obter dados. " + firebaseError(error)));
        }
      });
    };

    return { item, isLoading, runRequest };
  },

  ListFromUser: () => {
    const [isLoading, setLoading] = React.useState(false);

    const [items, setItems] = React.useState<ChatItem[] | null>(null);

    const runRequest = async (userId: string) => {
      return new Promise(async (Ok, Rej) => {
        try {
          setLoading(true);

          const mainRef = collection(db, "chats");
          const queryRef = query(
            mainRef,
            where("users", "array-contains", userId),
            where("visible", "==", true),
            orderBy("updatedAtTimestamp", "desc")
          );
          const mainSnap = await getDocs(queryRef);
          const listData = [] as any;

          mainSnap.forEach((doc) => {
            listData.push({ id: doc.id, ...doc.data() });
          });

          setItems(listData);
          setLoading(false);
          Ok(listData);
        } catch (error) {
          console.log(...new Dev("Request Error [listFromUser]", error).Error());
          setLoading(false);
          Rej(new Error("Erro ao obter dados. " + firebaseError(error)));
        }
      });
    };

    return { items, isLoading, runRequest };
  },

  ListAdmin: () => {
    const [isLoading, setLoading] = React.useState(false);

    const [items, setItems] = React.useState<ChatItem[] | null>(null);

    const runRequest = async () => {
      return new Promise(async (Ok, Rej) => {
        try {
          setLoading(true);

          const mainRef = collection(db, "chats");
          const queryRef = query(
            mainRef,
            where("isStaff", "==", true),
            orderBy("updatedAtTimestamp", "desc")
          );
          const mainSnap = await getDocs(queryRef);
          const listData = [] as any;

          mainSnap.forEach((doc) => {
            listData.push({ id: doc.id, ...doc.data() });
          });

          setItems(listData);
          setLoading(false);
          Ok(listData);
        } catch (error) {
          console.log(...new Dev("Request Error [listFromUser]", error).Error());
          setLoading(false);
          Rej(new Error("Erro ao obter dados. " + firebaseError(error)));
        }
      });
    };

    return { items, isLoading, runRequest };
  },

  Create: () => {
    const [isLoading, setLoading] = React.useState(false);

    const runRequest = async (item: SpottPostType) => {
      return new Promise(async (Ok, Rej) => {
        try {
          setLoading(true);

          const mainRef = collection(db, "chats");
          const mainSnap = await addDoc(mainRef, item);

          setLoading(false);
          Ok(true);
        } catch (error) {
          console.log(...new Dev("Request Error [create]", error).Error());
          setLoading(false);
          Rej(new Error("Erro ao criar dados. " + firebaseError(error)));
        }
      });
    };

    return { isLoading, runRequest };
  },

  Delete: () => {
    const [isLoading, setLoading] = React.useState(false);

    const runRequest = async (itemId: string) => {
      return new Promise(async (Ok, Rej) => {
        try {
          setLoading(true);

          const mainRef = doc(db, "chats", itemId);
          const mainSnap = await deleteDoc(mainRef);

          setLoading(false);
          Ok(true);
        } catch (error) {
          console.log(...new Dev("Request Error [delete]", error).Error());
          setLoading(false);
          Rej(new Error("Erro ao remover dados. " + firebaseError(error)));
        }
      });
    };

    return { isLoading, runRequest };
  },

  Update: () => {
    const [isLoading, setLoading] = React.useState(false);

    const runRequest = async (itemId: string, payload: any) => {
      return new Promise(async (Ok, Rej) => {
        try {
          setLoading(true);

          const mainRef = doc(db, "chats", itemId);
          const mainSnap = await updateDoc(mainRef, payload);

          setLoading(false);
          Ok(true);
        } catch (error) {
          console.log(...new Dev("Request Error [update]", error).Error());
          setLoading(false);
          Rej(new Error("Erro ao atualizar dados. " + firebaseError(error)));
        }
      });
    };

    return { isLoading, runRequest };
  },

  UpdateArrayAdd: () => {
    const [isLoading, setLoading] = React.useState(false);

    const runRequest = async (itemId: string, key: string, payload: any) => {
      return new Promise(async (Ok, Rej) => {
        try {
          setLoading(true);

          const mainRef = doc(db, "chats", itemId);
          const mainSnap = await updateDoc(mainRef, { [key]: arrayUnion(payload) });

          setLoading(false);
          Ok(true);
        } catch (error) {
          console.log(...new Dev("Request Error [updateArrayAdd]", error).Error());
          setLoading(false);
          Rej(new Error("Erro ao atualizar dados. " + firebaseError(error)));
        }
      });
    };

    return { isLoading, runRequest };
  },

  UpdateArrayRemove: () => {
    const [isLoading, setLoading] = React.useState(false);

    const runRequest = async (itemId: string, key: string, payload: any) => {
      return new Promise(async (Ok, Rej) => {
        try {
          setLoading(true);

          const mainRef = doc(db, "chats", itemId);
          const mainSnap = await updateDoc(mainRef, { [key]: arrayRemove(payload) });

          setLoading(false);
          Ok(true);
        } catch (error) {
          console.log(...new Dev("Request Error [updateArrayRemove]", error).Error());
          setLoading(false);
          Rej(new Error("Erro ao atualizar dados. " + firebaseError(error)));
        }
      });
    };

    return { isLoading, runRequest };
  },

  // Util Service

  LazyList: (definedLimit: number = 5) => {
    const [isLoading, setLoading] = React.useState(false);
    const [hasMore, setHasMore] = React.useState(false);

    const [lastItemRef, setLastItemRef] = React.useState<any>(null);
    const [firstItemRef, setFirstItemRef] = React.useState<any>(null);

    const [items, setItems] = React.useState<any[]>([]);

    const runRequest = async (chatId: string) => {
      return new Promise(async (Ok, Rej) => {
        try {
          setLoading(true);

          const queryRef = lastItemRef
            ? query(
                collection(db, "chats", chatId, "messages"),
                orderBy("serverTimestamp", "desc"),
                startAfter(lastItemRef),
                limit(definedLimit)
              )
            : query(
                collection(db, "chats", chatId, "messages"),
                orderBy("serverTimestamp", "desc"),
                limit(definedLimit)
              );

          const mainSnap = await getDocs(queryRef);

          setLastItemRef(mainSnap.docs[mainSnap.docs.length - 1] || null);

          const listData = [] as any;

          mainSnap.forEach((doc) => {
            listData.push({ id: doc.id, ...doc.data() });
          });

          console.log(listData);

          setItems([...items, ...listData]);

          if (listData.length < definedLimit) setHasMore(false);
          else setHasMore(true);

          if (!firstItemRef && mainSnap.docs.length) setFirstItemRef(mainSnap.docs[0]);

          setLoading(false);

          Ok(listData);
        } catch (error) {
          console.log(...new Dev("Request Error [list]", error).Error());
          setLoading(false);
          Rej(new Error("Erro ao obter dados. " + firebaseError(error)));
        }
      });
    };

    return { items, isLoading, runRequest, hasMore, lastItemRef, firstItemRef };
  },

  Subscribe: () => {
    const [isLoading, setLoading] = React.useState(false);

    const [items, setItems] = React.useState<ChatMessageItem[]>([]);

    const runRequest = async (chatId: string, lastMessageRef: any) => {
      return new Promise(async (Ok, Rej) => {
        try {
          if ((window as any).unsubSnaps) (window as any).unsubSnaps.map((item: any) => item());
          delete (window as any).unsubSnaps;

          setLoading(true);

          const queryRef = lastMessageRef
            ? query(
                collection(db, "chats", chatId, "messages"),
                orderBy("serverTimestamp", "asc"),
                startAfter(lastMessageRef)
              )
            : query(collection(db, "chats", chatId, "messages"), orderBy("serverTimestamp", "asc"));

          const unsubscribeSnap = onSnapshot(queryRef, (querySnapshot) => {
            console.log(...new Dev("Incomming Snapshot").Pink());

            setLoading(false);

            // const listData: any[] = [];
            // querySnapshot.forEach((doc) => {
            //   const docToAdd = { id: doc.id, ...doc.data() } as any;
            //   listData.push(docToAdd);
            // });
            // setItems(listData);

            querySnapshot.docChanges().forEach((change) => {
              if (change.type === "added") {
                console.log(...new Dev("New Message Snapshot").Info());

                const listData: any[] = [];
                querySnapshot.forEach((doc) => {
                  const docToAdd = { id: doc.id, ...doc.data() } as any;
                  listData.push(docToAdd);
                });
                setItems(listData);
              }

              // if (change.type === "modified")
              //   console.log(...new Dev("Modified Message Snapshot", change.doc.data()).Info());

              // if (change.type === "removed")
              //   console.log(...new Dev("Removed Message Snapshot", change.doc.data()).Info());
            });
          });

          if ("unsubSnaps" in window)
            window.unsubSnaps = [...(window as any).unsubSnaps, unsubscribeSnap];
          else (window as any).unsubSnaps = [unsubscribeSnap];

          Ok(null);
        } catch (error) {
          console.log(...new Dev("Request Error [one]", error).Error());
          setLoading(false);
          Rej(new Error("Erro ao obter dados. " + firebaseError(error)));
        }
      });
    };

    return { items, isLoading, runRequest };
  },

  Send: () => {
    const [isLoading, setLoading] = React.useState(false);

    const runRequest = async (chatId: string, item: any) => {
      return new Promise(async (Ok, Rej) => {
        try {
          setLoading(true);

          const mainRef = collection(db, "chats", chatId, "messages");
          const mainSnap = await addDoc(mainRef, item);

          setLoading(false);
          Ok(true);
        } catch (error) {
          console.log(...new Dev("Request Error [create]", error).Error());
          setLoading(false);
          Rej(new Error("Erro ao criar dados. " + firebaseError(error)));
        }
      });
    };

    return { isLoading, runRequest };
  },
};
